import { useCallback, useMemo, useRef, useState, type DragEvent } from "react";
import ENV from "@/utils/env";
import { cn } from "@/utils/classes";
import { asFileSize } from "@/utils/format";
import { useSegment } from "@/hooks/use-segment";
import { useCreateAsset, useDeleteAsset, useGetAssets, useUpdateAsset } from "@/api/studio/assets/queries";
import { stageUpload, uploadUpload } from "@/api/studio/uploads/api";
import type { Asset } from "@/api/studio/assets/types/asset.type";
import { EditElement } from "@/features/studies/components/edit-element";
import { Button } from "../button";
import { Table, TableCell, TableHeader, TableRow } from "../table";
import { Toolbar, ToolbarItem } from "../toolbar";
import { ArrowPathIcon, CheckCircleIcon, ExclamationCircleIcon, EyeSlashIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import { Loading } from "@/components/fallbacks/loading";

/* Constants */
const ACCEPTED_FILE_TYPES = [".jpg", ".jpeg", ".png", ".tiff", ".tif"];
const MAX_DIMENSION = 4096;

/* Types */
type UploadStatus = "uploading" | "failed" | "invalid";

interface UploadingAsset {
  key: string;
  file: File;
  name: string;
  width: number;
  height: number;
  status: UploadStatus;
  errorMessage?: string;
}

/* Prop Types */
interface AssetUploadDropzoneProps {
  projectId: string;
  studyId: string;
  additionalSegmentProperties?: Record<string, any>;
  segmentTrackName: string;
  segmentTrackMediaType: string;
  dragDropVideoUrl: string;
  dragDropVideoClassName?: string;
  dragDropText: string;
  selectAssetsText?: string;
  firstColumnHeader?: string;
}

/*
 * AssetUploadDropzone component
 * Query-driven drag and drop uploader that uploads immediately and initializes from existing assets.
 */
export const AssetUploadDropzone = ({
  projectId,
  studyId,
  additionalSegmentProperties,
  segmentTrackName,
  segmentTrackMediaType,
  dragDropVideoUrl,
  dragDropVideoClassName,
  dragDropText,
  selectAssetsText = "Select assets",
  firstColumnHeader = "Asset",
}: AssetUploadDropzoneProps) => {
  /* Refs */
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  /* State */
  const [isDragging, setIsDragging] = useState(false);
  const [uploadingAssets, setUploadingAssets] = useState<Array<UploadingAsset>>([]);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Queries */
  const { data: assetsData, isLoading: isAssetsLoading } = useGetAssets(projectId, studyId);
  const createAssetMutation = useCreateAsset();
  const updateAssetMutation = useUpdateAsset();
  const deleteAssetMutation = useDeleteAsset();

  /* Derived state */
  const existingAssets = assetsData?.assets ?? [];
  const hasRows = !!existingAssets.length || !!uploadingAssets.length;

  /* Helpers */
  const getImageDimensions = (file: File) => {
    return new Promise<{ width: number; height: number }>((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.src = URL.createObjectURL(file);
    });
  };

  /* Upload worker */
  const uploadAsset = useCallback(
    async (asset: UploadingAsset) => {
      // Update the asset status to uploading
      setUploadingAssets((prev) => prev.map((row) => (row.key === asset.key ? { ...row, status: "uploading", errorMessage: undefined } : row)));

      try {
        // Stage and upload the file in S3
        const { url, key } = await stageUpload(projectId, studyId);
        await uploadUpload({ key, file: asset.file, url });

        // Create the asset in the database
        await createAssetMutation.mutateAsync({
          projectId,
          studyId,
          values: {
            pipelineKey: key,
            name: asset.name,
            filename: asset.file.name,
          },
        });

        // Remove the asset from the uploading list once it's successfully uploaded and created
        setUploadingAssets((prev) => prev.filter((row) => row.key !== asset.key));

        // Segment event
        segmentTrackEvent("Media uploaded", {
          projectId,
          studyId,
          studyType: "asset",
          fileName: asset.name,
        });
      } catch {
        // Update the asset status to failed and set an error message
        setUploadingAssets((prev) =>
          prev.map((row) => (row.key === asset.key ? { ...row, status: "failed", errorMessage: "We had an issue uploading this asset, please try again." } : row)),
        );
      }
    },
    [createAssetMutation, projectId, segmentTrackEvent, studyId],
  );

  /* Handlers */
  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleAdd = useCallback(
    async (files: File[]) => {
      for (const file of files) {
        const dims = await getImageDimensions(file);

        // Segment event
        segmentTrackEvent(`${segmentTrackName} ${segmentTrackMediaType} Staged`, {
          ...additionalSegmentProperties,
          fileName: file.name,
          fileSize: file.size,
          width: dims.width,
          height: dims.height,
        });

        // Check if the image exceeds max dimensions and if so, mark it as invalid without attempting to upload
        const exceedsMaxDimension = dims.width > MAX_DIMENSION || dims.height > MAX_DIMENSION;
        const row: UploadingAsset = {
          key: crypto.randomUUID(),
          file,
          name: file.name,
          width: dims.width,
          height: dims.height,
          status: exceedsMaxDimension ? "invalid" : "uploading",
          errorMessage: exceedsMaxDimension ? `Image dimensions are larger than ${MAX_DIMENSION} x ${MAX_DIMENSION}px` : undefined,
        };

        // Add the new asset to the uploading list
        setUploadingAssets((prev) => [...prev, row]);

        // If the image is valid, start the upload process
        if (!exceedsMaxDimension) {
          void uploadAsset(row);
        }
      }

      // Clear the file input value to allow uploading the same file again if needed
      if (inputRef.current) inputRef.current.value = "";
    },
    [additionalSegmentProperties, segmentTrackEvent, segmentTrackMediaType, segmentTrackName, uploadAsset],
  );

  const handleRetry = (key: string) => {
    const asset = uploadingAssets.find((row) => row.key === key);
    if (!asset || asset.status === "invalid") return;
    void uploadAsset(asset);
  };

  const handleRemoveUploading = (row: UploadingAsset) => {
    segmentTrackEvent(`${segmentTrackName} Staged ${segmentTrackMediaType} Removed`, {
      ...additionalSegmentProperties,
      fileName: row.name,
    });
    setUploadingAssets((prev) => prev.filter((asset) => asset.key !== row.key));
  };

  const handleRemoveExisting = (asset: Asset) => {
    segmentTrackEvent(`${segmentTrackName} ${segmentTrackMediaType} Removed`, {
      ...additionalSegmentProperties,
      fileName: asset.name,
    });

    deleteAssetMutation.mutate({
      projectId,
      studyId,
      assetId: asset.id,
    });
  };

  const handleClear = async () => {
    segmentTrackEvent(`${segmentTrackName} Staged ${segmentTrackMediaType}s Cleared`, {
      ...additionalSegmentProperties,
    });

    setUploadingAssets([]);

    await Promise.allSettled(
      existingAssets.map((asset) =>
        deleteAssetMutation.mutateAsync({
          projectId,
          studyId,
          assetId: asset.id,
        }),
      ),
    );
  };

  const handleRenameUploading = (key: string, newName: string) => {
    setUploadingAssets((prev) => prev.map((row) => (row.key === key ? { ...row, name: newName } : row)));
  };

  const handleRenameExisting = (asset: Asset, newName: string) => {
    if (newName === asset.name) return;

    segmentTrackEvent(`${segmentTrackName} ${segmentTrackMediaType} Renamed`, {
      ...additionalSegmentProperties,
      from: asset.name,
      to: newName,
    });

    updateAssetMutation.mutate({
      projectId,
      studyId,
      assetId: asset.id,
      values: {
        name: newName,
        filename: asset.filename,
      },
    });
  };

  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      const files = Array.from(e.dataTransfer.files);
      const validFiles = files.filter((file) => {
        const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
        return ACCEPTED_FILE_TYPES.some((type) => type.toLowerCase() === fileExtension);
      });

      if (validFiles.length) {
        await handleAdd(validFiles);
      }
    },
    [handleAdd],
  );

  const dragBindings = useMemo(
    () => ({
      onDragEnter: (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current += 1;
        setIsDragging(true);
      },
      onDragOver: (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
      },
      onDragLeave: (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        dragCounter.current -= 1;
        if (dragCounter.current === 0) setIsDragging(false);
      },
      onDrop: handleDrop,
    }),
    [handleDrop],
  );

  /* Render */
  return (
    <>
      {/* Tertiary Toolbar */}
      {hasRows && !isAssetsLoading && (
        <Toolbar>
          <ToolbarItem>
            <div className="flex flex-row gap-2">
              <Button disabled={!hasRows} onClick={handleClear}>
                Clear All
              </Button>
              <Button variant="primary" onClick={handleClick}>
                <PlusIcon className="size-5 text-neutral" />
                Add
              </Button>
            </div>
          </ToolbarItem>
        </Toolbar>
      )}

      {/* Loading state while fetching existing assets */}
      {isAssetsLoading && !uploadingAssets.length && (
        <div className="w-full flex flex-col justify-center gap-3 py-9 h-96 items-center border-2 border-dashed border-base-300 rounded-lg">
          <video src={dragDropVideoUrl} className={cn("w-36", dragDropVideoClassName)} autoPlay muted loop />
          <h3 className="text-base-content/70">Loading assets...</h3>
        </div>
      )}

      {/* Empty state with drag and drop */}
      {!isAssetsLoading && !hasRows && (
        <div
          className={cn(
            "w-full flex flex-col justify-center gap-6 py-9 h-96 items-center border-2 border-dashed border-base-300 rounded-lg",
            isDragging && "border-primary bg-base-200",
          )}
          {...dragBindings}
        >
          <video src={dragDropVideoUrl} className={cn("w-36", dragDropVideoClassName)} autoPlay muted loop />
          {!isDragging && (
            <div className="flex flex-col gap-3 items-center">
              <h2>Drag & drop</h2>
              <p className="text-base-content/70">{dragDropText}</p>
            </div>
          )}

          {isDragging && <p>Drop files here</p>}
          <Button variant="primary" onClick={handleClick}>
            {selectAssetsText}
          </Button>
        </div>
      )}

      {/* Uploads */}
      {hasRows && !isAssetsLoading && (
        <div className="grow flex flex-row flex-wrap w-full gap-4 p-2">
          <Table>
            <thead>
              <TableRow>
                <TableHeader>{firstColumnHeader}</TableHeader>
                <TableHeader className="w-40">Size</TableHeader>
                <TableHeader>Dimensions</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader className="w-40"></TableHeader>
              </TableRow>
            </thead>
            <tbody>
              {uploadingAssets.map((upload) => (
                <TableRow key={upload.key} className="group hover:bg-base-100/50 cursor-pointer">
                  <TableCell truncate={false}>
                    {upload.status !== "invalid" ? (
                      <div className="skeleton w-16 h-16 rounded-lg bg-base-300"></div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-base-300 flex items-center justify-center">
                        <EyeSlashIcon className="size-7 text-error" />
                      </div>
                    )}
                    <EditElement defaultValue={upload.name} elementId={upload.key} handleElementEdit={handleRenameUploading}>
                      <p className="truncate">{upload.name}</p>
                    </EditElement>
                  </TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell>
                    {upload.status === "uploading" && (
                      <div className="flex items-center gap-2 text-base-content/70">
                        <Loading size="sm" />
                        Uploading...
                      </div>
                    )}
                    {upload.status === "failed" && (
                      <div className="flex items-center gap-2 text-error">
                        <ExclamationCircleIcon className="size-4" />
                        {upload.errorMessage ?? "We had an issue uploading this asset, please try again"}
                      </div>
                    )}
                    {upload.status === "invalid" && (
                      <div className="flex items-center gap-2 text-error">
                        <ExclamationCircleIcon className="size-4" />
                        {upload.errorMessage ?? "This asset is invalid and cannot be uploaded."}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="justify-end">
                    {upload.status === "failed" && (
                      <Button className="h-8 min-h-8 max-h-8" onClick={() => handleRetry(upload.key)}>
                        <ArrowPathIcon className="size-4" />
                        Retry
                      </Button>
                    )}
                    <TrashIcon className="size-4" onClick={() => handleRemoveUploading(upload)} />
                  </TableCell>
                </TableRow>
              ))}

              {/* Existing Assets */}
              {existingAssets.map((asset) => (
                <TableRow key={asset.id} className="group hover:bg-base-100/50 cursor-pointer">
                  <TableCell truncate={false}>
                    <img
                      src={`${ENV.API_PLATFORM_URL}/pipeline/renderer/v1/images?prefix=${projectId}/${studyId}&key=${asset.pipelineKey}&maxWidth=150&maxHeight=150`}
                      className="rounded-lg w-16 h-16 object-contain"
                    />
                    <EditElement defaultValue={asset.name} elementId={asset.id} handleElementEdit={(_, newName) => handleRenameExisting(asset, newName)}>
                      <p className="truncate">{asset.name}</p>
                    </EditElement>
                  </TableCell>
                  <TableCell>{asFileSize(asset.sizeInBytes)}</TableCell>
                  <TableCell>{`${asset.width} × ${asset.height} px`}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 text-success">
                      <CheckCircleIcon className="size-4" />
                      Ready
                    </div>
                  </TableCell>
                  <TableCell className="justify-end">
                    <TrashIcon className="size-4" onClick={() => handleRemoveExisting(asset)} />
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Hidden */}
      <input
        multiple
        type="file"
        accept={ACCEPTED_FILE_TYPES.join(", ")}
        className="hidden"
        ref={inputRef}
        onChange={(e) => {
          void handleAdd(Array.from(e.target.files ?? []));
        }}
      />
    </>
  );
};
